'use client';

import { Check } from 'lucide-react';
import { Fragment } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter } from '@/i18n/navigation';

export type RegistrationProgressStepId =
  | 'registration'
  | 'cards'
  | 'activities'
  | 'checkout'
  | 'payment'
  | 'dashboard';

type StepId = RegistrationProgressStepId;

interface RegistrationProgressBarProps {
  currentStep: RegistrationProgressStepId;
}

/** Путь без префикса локали (как у `usePathname` из next-intl). */
export function resolveRegistrationProgressStep(
  pathname: string,
): RegistrationProgressStepId {
  const path = (pathname || '/').replace(/\/$/, '') || '/';
  if (
    path.endsWith('/registration-flow') ||
    path.endsWith('/profile-registration')
  ) {
    return 'registration';
  }
  if (path.endsWith('/registration-cards')) return 'cards';
  if (path.endsWith('/activities')) return 'activities';
  if (path.includes('/order-confirmation')) return 'payment';
  if (path.endsWith('/confirmation')) return 'payment';
  if (path.endsWith('/cart')) return 'checkout';
  if (path.endsWith('/dashboard')) return 'dashboard';
  return 'registration';
}

const STEPS: { id: StepId; path: string }[] = [
  { id: 'registration', path: '/registration-flow' },
  { id: 'cards', path: '/registration-cards' },
  { id: 'activities', path: '/activities' },
  { id: 'checkout', path: '/cart' },
  { id: 'payment', path: '/cart' },
  { id: 'dashboard', path: '/dashboard' },
];

function stepLabels(
  t: ReturnType<typeof useTranslations<'strings.registrationProgress'>>,
  id: StepId,
): { label: string; shortLabel: string } {
  switch (id) {
    case 'registration':
      return { label: t('registrationLabel'), shortLabel: t('registrationShort') };
    case 'cards':
      return { label: t('cardsLabel'), shortLabel: t('cardsShort') };
    case 'activities':
      return { label: t('activitiesLabel'), shortLabel: t('activitiesShort') };
    case 'checkout':
      return { label: t('checkoutLabel'), shortLabel: t('checkoutShort') };
    case 'payment':
      return { label: t('paymentLabel'), shortLabel: t('paymentShort') };
    case 'dashboard':
      return { label: t('dashboardLabel'), shortLabel: t('dashboardShort') };
    default:
      return { label: '', shortLabel: '' };
  }
}

export function RegistrationProgressBar({ currentStep }: RegistrationProgressBarProps) {
  const router = useRouter();
  const t = useTranslations('strings.registrationProgress');
  const currentIndex = STEPS.findIndex((s) => s.id === currentStep);

  const handleStepClick = (index: number, path: string) => {
    if (index < currentIndex) router.push(path);
  };

  return (
    <div className="border-b border-fest-border-alt bg-fest-canvas shadow-sm">
      <div className="container mx-auto max-w-7xl px-2 py-2 sm:px-4 sm:py-3 md:py-4">
        {/* Узкие экраны: горизонтальный скролл, фиксированная ширина колонки — подписи не сжимаются */}
        <div
          className="touch-pan-x overflow-x-auto pb-0.5 [-webkit-overflow-scrolling:touch] xl:hidden"
          aria-label={t('ariaScrollHint')}
        >
          <div className="flex min-w-min items-stretch pe-2">
            {STEPS.map((step, index) => {
              const isCompleted = index < currentIndex;
              const isCurrent = index === currentIndex;
              const { label, shortLabel } = stepLabels(t, step.id);
              const labelTone =
                isCompleted || isCurrent ? 'text-fest-heading' : 'text-fest-subtle-text';
              const labelClass = `${labelTone} ${isCompleted ? 'hover:text-fest-muted-text' : ''}`;
              const segmentAfterCompleted = index < currentIndex;
              const hasConnector = index < STEPS.length - 1;

              return (
                <Fragment key={step.id}>
                  <div className="flex w-[4.85rem] shrink-0 flex-col sm:w-[5.25rem]">
                    <div
                      role={isCompleted ? 'button' : undefined}
                      tabIndex={isCompleted ? 0 : undefined}
                      className={`flex flex-col items-center gap-1 px-0.5 pt-0.5 ${isCompleted ? 'cursor-pointer' : ''}`}
                      onClick={() => handleStepClick(index, step.path)}
                      onKeyDown={(e) => {
                        if (!isCompleted) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleStepClick(index, step.path);
                        }
                      }}
                      aria-label={label}
                    >
                      <div
                        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-all sm:h-9 sm:w-9 ${
                          isCompleted
                            ? 'bg-fest-muted-text text-white shadow-md hover:shadow-lg'
                            : isCurrent
                              ? 'bg-fest-heading text-white shadow-lg'
                              : 'border-2 border-fest-border-alt bg-white text-fest-subtle-text'
                        }`}
                      >
                        {isCompleted ? (
                          <Check className="h-3.5 w-3.5 sm:h-4 sm:w-4" strokeWidth={3} />
                        ) : (
                          <span>{index + 1}</span>
                        )}
                      </div>
                      <p
                        className={`max-w-[4.75rem] hyphens-auto text-balance text-center text-[10px] font-bold leading-snug sm:max-w-[5.1rem] sm:text-[11px] ${labelClass}`}
                      >
                        {shortLabel}
                      </p>
                    </div>
                    <div className="mt-1.5 h-1 w-full shrink-0 overflow-hidden rounded-sm">
                      <div
                        className={`h-full w-full ${isCurrent ? 'bg-fest-heading' : 'bg-transparent'}`}
                      />
                    </div>
                  </div>
                  {hasConnector ? (
                    <div className="flex w-2.5 shrink-0 flex-col justify-end pb-2 sm:w-3" aria-hidden>
                      <div
                        className={`h-0.5 w-full rounded-full ${
                          segmentAfterCompleted ? 'bg-fest-muted-text' : 'bg-fest-border-alt'
                        }`}
                      />
                    </div>
                  ) : null}
                </Fragment>
              );
            })}
          </div>
        </div>

        {/* xl+: одна строка с полными подписями; md–2xl часто не хватает ширины под 6 русских названий */}
        <div className="hidden min-w-0 w-full items-stretch xl:flex">
          {STEPS.map((step, index) => {
            const isCompleted = index < currentIndex;
            const isCurrent = index === currentIndex;
            const { label } = stepLabels(t, step.id);
            const labelTone =
              isCompleted || isCurrent ? 'text-fest-heading' : 'text-fest-subtle-text';
            const labelClass = `${labelTone} ${isCompleted ? 'hover:text-fest-muted-text' : ''}`;
            const segmentAfterCompleted = index < currentIndex;
            const hasConnector = index < STEPS.length - 1;

            return (
              <div key={step.id} className="flex min-w-0 flex-1 flex-col">
                <div className="flex min-w-0 w-full items-center">
                  <div
                    className={`flex min-w-0 max-w-full shrink items-center gap-2 overflow-hidden ${isCompleted ? 'cursor-pointer' : ''}`}
                    onClick={() => handleStepClick(index, step.path)}
                    onKeyDown={(e) => {
                      if (!isCompleted) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleStepClick(index, step.path);
                      }
                    }}
                    role={isCompleted ? 'button' : undefined}
                    tabIndex={isCompleted ? 0 : undefined}
                    aria-label={label}
                  >
                    <div
                      className={`flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full text-sm font-bold transition-all ${
                        isCompleted
                          ? 'bg-fest-muted-text text-white shadow-md hover:shadow-lg hover:scale-105'
                          : isCurrent
                            ? 'bg-fest-heading text-white shadow-lg'
                            : 'border-2 border-fest-border-alt bg-white text-fest-subtle-text'
                      }`}
                    >
                      {isCompleted ? (
                        <Check className="h-5 w-5" strokeWidth={3} />
                      ) : (
                        <span>{index + 1}</span>
                      )}
                    </div>
                    <p
                      className={`min-w-0 max-w-full truncate text-sm font-bold leading-tight transition-all ${labelClass}`}
                    >
                      {label}
                    </p>
                  </div>
                  {hasConnector && (
                    <div className="ms-2 flex h-10 min-w-[6px] flex-1 items-center" aria-hidden>
                      <div
                        className={`h-0.5 w-full min-w-[6px] ${
                          segmentAfterCompleted ? 'bg-fest-muted-text' : 'bg-fest-border-alt'
                        }`}
                      />
                    </div>
                  )}
                </div>
                <div className="mt-2 h-1 w-full shrink-0">
                  <div
                    className={`h-full w-full ${isCurrent ? 'bg-fest-heading' : 'bg-transparent'}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
